import { useEffect, useRef, useState, type MutableRefObject } from "react";

import { QRCodeSVG } from "qrcode.react";

import { Button, Loader, Notification } from "@stellar/design-system";

import { ModalDownloadWallet } from "@/components/ModalDownloadWallet";

import type { EngineTypes, SessionTypes, SignClientTypes } from "@via/walletconnect-types";

import VIAWalletLogoSrc from "@/assets/VIA-wallet-logo.svg";
import {
  DEFAULT_CONNECT_PARAMS,
  STELLAR_MAINNET_CHAIN_ID,
  STELLAR_METHOD_SIGN_XDR,
} from "@/services/walletConnect/constants";
import { WcSignClient } from "@/services/walletConnect/wcSignClient";

type ConnState = "initializing" | "awaiting-scan" | "connecting" | "connected" | "error";

export type CoSignerKeyResult = {
  publicKey: string;
  session: SessionTypes.Struct;
};

type CoSignerKeyStepProps = {
  userId: string;
  onConnected: (result: CoSignerKeyResult) => void;
  onDisconnected: () => void;
  disconnectRef?: MutableRefObject<(() => Promise<void>) | null>;
  signRef?: MutableRefObject<((xdr: string) => Promise<string>) | null>;
};

const extractStellarPublicKey = (session: SessionTypes.Struct): string | null => {
  // WalletConnect namespace accounts are `stellar:<network>:<publicKey>`.
  const stellarNamespace = session.namespaces?.stellar;
  const account = stellarNamespace?.accounts?.[0];
  if (!account) return null;
  const parts = account.split(":");
  return parts[parts.length - 1] || null;
};

const truncateKey = (key: string): string =>
  key.length > 12 ? `${key.slice(0, 6)}...${key.slice(-4)}` : key;

/**
 * Read the signed XDR out of whatever shape `stellar_signXDR` returns.
 * TODO(sdp-fe): track the wallet stellar support implementation
 */
export const extractSignedXdr = (response: unknown): string | null => {
  if (typeof response === "string") return response;
  if (response && typeof response === "object") {
    const r = response as Record<string, unknown>;
    if (typeof r.signedXDR === "string") return r.signedXDR;
    if (typeof r.signed_xdr === "string") return r.signed_xdr;
    if (typeof r.xdr === "string") return r.xdr;
    if (r.result && typeof r.result === "object") return extractSignedXdr(r.result);
  }
  return null;
};

export const CoSignerKeyStep = ({
  userId,
  onConnected,
  onDisconnected,
  disconnectRef,
  signRef,
}: CoSignerKeyStepProps) => {
  const [state, setState] = useState<ConnState>("initializing");
  const [uri, setUri] = useState<string | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasCopied, setHasCopied] = useState<boolean>(false);
  const [isDownloadWalletModalOpen, setIsDownloadWalletModalOpen] = useState(false);

  const clientRef = useRef<WcSignClient | null>(null);
  const unsubscribersRef = useRef<Array<() => void>>([]);
  const isMountedRef = useRef(true);

  const cleanupSubscriptions = () => {
    unsubscribersRef.current.forEach((fn) => fn());
    unsubscribersRef.current = [];
  };

  const disconnectClient = async () => {
    try {
      await clientRef.current?.disconnect();
    } catch {
      // Best-effort teardown — ignore disconnect errors.
    }
  };
  const handleCopyUri = async () => {
    if (!uri) return;
    try {
      await navigator.clipboard.writeText(uri);
      setHasCopied(true);
      setTimeout(() => {
        if (isMountedRef.current) setHasCopied(false);
      }, 2000);
    } catch (e) {
      console.error(e, "Failed to copy URI to clipboard");
    }
  };
  const startPairing = async (newConnection: boolean) => {
    const client = clientRef.current;
    if (!client) return;
    setError(null);
    setUri(null);
    setPublicKey(null);
    setState("initializing");
    try {
      const params: EngineTypes.ConnectParams = { ...DEFAULT_CONNECT_PARAMS };
      const nextUri = await client.connect(
        params,
        newConnection,
        (_p, session) => {
          if (!isMountedRef.current) return;
          const pk = extractStellarPublicKey(session);
          if (!pk) {
            setError("VIA Wallet approved the session but did not return a Stellar account.");
            setState("error");
            return;
          }
          setPublicKey(pk);
          setState("connected");
          onConnected({ publicKey: pk, session });
        },
        (_p, e) => {
          if (!isMountedRef.current) return;
          setError(e.message || "The pairing was rejected.");
          setState("error");
        },
      );

      if (nextUri) {
        setUri(nextUri);
        setState("awaiting-scan");
      } else {
        // Already had a session (WC restored it from storage).
        const existing = client.getSession();
        if (existing) {
          const pk = extractStellarPublicKey(existing);
          if (pk) {
            setPublicKey(pk);
            setState("connected");
            onConnected({ publicKey: pk, session: existing });
            return;
          }
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setState("error");
    }
  };

  useEffect(() => {
    isMountedRef.current = true;

    const initialize = async () => {
      try {
        const client = await WcSignClient.init(userId);
        if (!isMountedRef.current) {
          // Component unmounted mid-init — dispose immediately.
          await client.disconnect().catch(() => undefined);
          return;
        }
        clientRef.current = client;
        // React to session lifecycle events so we surface disconnects.
        const events: SignClientTypes.Event[] = ["session_delete", "session_expire"];
        events.forEach((event) => {
          const unsub = client.on(event, () => {
            if (!isMountedRef.current) return;
            setPublicKey(null);
            setState("awaiting-scan");
            onDisconnected();
          });
          unsubscribersRef.current.push(unsub);
        });

        const restored = client.getSession();
        if (restored) {
          const pk = extractStellarPublicKey(restored);
          if (pk) {
            setPublicKey(pk);
            setState("connected");
            onConnected({ publicKey: pk, session: restored });
            return;
          }
        }

        await startPairing(true);
      } catch (err) {
        if (!isMountedRef.current) return;
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        setState("error");
      }
    };

    void initialize();

    return () => {
      isMountedRef.current = false;
      cleanupSubscriptions();
      void disconnectClient();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!disconnectRef) return;
    disconnectRef.current = async () => {
      await disconnectClient();
      if (isMountedRef.current) {
        setPublicKey(null);
        setState("awaiting-scan");
      }
      onDisconnected();
    };
    return () => {
      if (disconnectRef) disconnectRef.current = null;
    };
    // `disconnectClient` and `onDisconnected` are captured for the lifetime of
    // the step; keeping them out of deps mirrors the init effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disconnectRef]);

  useEffect(() => {
    if (!signRef) return;
    /**
     * Send `stellar_signXDR` over the live WC session. Returns the signed XDR
     * as a string. Callers own the "is the wallet still connected?"
     * TODO(sdp-fe): track the wallet implementation
     */
    signRef.current = (xdr: string) =>
      new Promise<string>((resolve, reject) => {
        const client = clientRef.current;
        if (!client) {
          reject(new Error("VIA Wallet is not connected."));
          return;
        }
        void client.sendRequest(
          STELLAR_MAINNET_CHAIN_ID,
          { method: STELLAR_METHOD_SIGN_XDR, params: [{ xdr }] },
          (_p, response) => {
            const signed = extractSignedXdr(response);
            if (!signed) {
              reject(new Error("VIA Wallet returned an unexpected response for stellar_signXDR."));
              return;
            }
            resolve(signed);
          },
          (_p, err) => reject(err),
        );
      });
    return () => {
      if (signRef) signRef.current = null;
    };
  }, [signRef]);

  const handleChangeKey = () => {
    void (async () => {
      await disconnectClient();
      onDisconnected();
      await startPairing(true);
    })();
  };

  const handleRetry = () => {
    void startPairing(true);
  };

  return (
    <>
      <h2 className="MultisigSetup__title" style={{ textAlign: "left" }}>
        Set up your co-signing key
      </h2>
      <p className="MultisigSetup__p">These steps happen in VIA Wallet on your device.</p>
      <div className="MultisigStepsList">
        <span>
          1. Sign into VIA Wallet. Don't have VIA Wallet?{" "}
          <button
            type="button"
            className="MultisigStepsList__linkButton"
            onClick={() => setIsDownloadWalletModalOpen(true)}
          >
            Download VIA Wallet
          </button>{" "}
          to your mobile device.
        </span>
        <span>
          2. Scan the QR code in VIA Wallet to connect your VIA Wallet to VIA Disbursement Platform.
        </span>
        <span>
          3. Choose an existing key or create a new key to be your Co-signing Key in VIA Wallet. You
          will need to choose this key to sign off on future disbursements.
        </span>
      </div>

      {error && (
        <Notification variant="error" title="WalletConnect error" isFilled>
          {error}
        </Notification>
      )}
      <div className="MultisigConnect">
        {state === "initializing" && (
          <>
            <div className="MultisigConnect__qrFrame">
              {/* Skeleton placeholder matching the 220x220 QR Code */}
              <div
                style={{
                  width: 220,
                  height: 220,
                  backgroundColor: "var(--sds-clr-gray-2, #e3e6e8)", // Uses Stellar gray or fallback
                  borderRadius: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0.7,
                }}
              >
                <Loader size="2rem" />
              </div>
            </div>
            <div className="MultisigConnect__pending">
              <span>Preparing pairing…</span>
            </div>
          </>
        )}
        {state === "awaiting-scan" && uri && (
          <>
            <div className="MultisigConnect__qrFrame">
              <QRCodeSVG
                value={uri}
                size={220}
                level="M"
                includeMargin={false}
                imageSettings={{
                  src: VIAWalletLogoSrc,
                  height: 32,
                  width: 32,
                  excavate: true,
                }}
              />
            </div>
            <div className="MultisigConnect__pending">
              <Button variant="tertiary" size="sm" onClick={handleCopyUri}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  {hasCopied ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  )}
                  {hasCopied ? "Copied!" : "Copy URI"}
                </div>
              </Button>
            </div>
          </>
        )}

        {state === "connecting" && (
          <div className="MultisigConnect__pending">
            <Loader size="1rem" />
            <span>Connecting…</span>
          </div>
        )}

        {state === "connected" && publicKey && (
          <div className="MultisigConnect__state">
            <div className="MultisigConnect__stateHead">
              <span className="MultisigConnect__check" aria-hidden>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  name="check"
                >
                  <path d="M20 6 9 17l-5-5"></path>
                </svg>
              </span>
              You're using <strong style={{ fontWeight: "bold" }}>Disbursement signing key</strong>{" "}
              to co-sign
            </div>
            <div className="MultisigConnect__label">Public key address</div>
            <code className="MultisigConnect__code" title={publicKey}>
              {truncateKey(publicKey)}
            </code>
            <div>
              <Button variant="tertiary" size="md" onClick={handleChangeKey}>
                Change key
              </Button>
            </div>
          </div>
        )}

        {state === "error" && (
          <Button variant="primary" size="sm" onClick={handleRetry}>
            Retry pairing
          </Button>
        )}
      </div>
      {/* Reserved for future multi-chain support. */}
      <span hidden>{STELLAR_MAINNET_CHAIN_ID}</span>

      <ModalDownloadWallet
        visible={isDownloadWalletModalOpen}
        onClose={() => setIsDownloadWalletModalOpen(false)}
      />
    </>
  );
};
